'use client';

import { handle_GTM_65 } from '@gtm/65';
import { handle_GTM_69 } from '@gtm/69';
import classNames from 'classnames';
import GloLayout from 'code-micro/common/GloLayoutA';
import { ContentServerProps } from 'code-micro/props/content';
import { useIntersectionObservers } from 'eco-design-micro/hooks/useIntersectionObservers';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import { useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/navigation';
import { Swiper as ItfSwiper } from 'swiper/types';
import Arrow from '../../product-series/content/icons';
import { ItfServiceSupportData, ItfServiceSupportItem } from '../setting/type';
import './index.scss';

interface ItfAniArrItem {
    name: string;
    cls: string;
}

export default function Content({ id, data = {}, isEditStatus }: ContentServerProps) {
    const { theme, title, titleColor, desc, descColor, items = [] as ItfServiceSupportItem[] } = data as ItfServiceSupportData;
    const prefixCls = 'service-support';
    const [swiper, setSwiper] = useState<ItfSwiper | null>(null);
    const [enter, setEnter] = useState({
        title: isEditStatus,
        desc: isEditStatus,
        content: isEditStatus,
    });
    const swiperRef = useRef<any>(null);

    const arr: ItfAniArrItem[] = [
        { name: 'title', cls: `${prefixCls}-info-title` },
        { name: 'desc', cls: `${prefixCls}-info-desc` },
        { name: 'content', cls: `${prefixCls}-swiper-box` },
    ];

    useIntersectionObservers(
        {
            selectors: [
                `.editor-${prefixCls}-${id} .${prefixCls}-info-title`,
                `.editor-${prefixCls}-${id} .${prefixCls}-info-desc`,
                `.editor-${prefixCls}-${id} .${prefixCls}-swiper-box`,
            ],
            onEnter: (e: Element) => {
                const aim: ItfAniArrItem | undefined = arr?.find((i: ItfAniArrItem) => i.cls === e.classList?.[0]);
                if (aim) {
                    setEnter((prev) => ({ ...prev, [aim.name]: true }));
                }
            },
            // onLeave: (e: Element) => {
            //     const aim: ItfAniArrItem | undefined = arr?.find((i: ItfAniArrItem) => i.cls === e.classList?.[0]);
            //     if (aim) {
            //         setEnter((prev) => ({ ...prev, [aim.name]: false }));
            //     }
            // },
        },
        [],
        isEditStatus,
    );

    const handle_GTM_Event = (item: ItfServiceSupportItem) => {
        const { text, url } = item;
        handle_GTM_65({
            navigation_name: text,
            position: `Series_ServiceSupport_${id}`,
            __btn_id1: `editor-${prefixCls}-${id}`,
            __btn_id2: url,
            __btn_type: 'href',
        });
    };

    const handleGTM = (btn: string) => {
        handle_GTM_69({
            button_name: btn,
            position: `Series_ServiceSupport-${id}`,
            __btn_id1: `editor-${prefixCls}-${id}`,
            __btn_id2: btn,
            __btn_type: 'common',
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined' && swiper) {
            swiper.allowTouchMove = window.innerWidth >= 768;
        }
    }, [swiper]);

    useEffect(() => {
        if (swiperRef.current && swiper) {
            const prev = swiperRef.current.querySelector('.swiper-button-prev');
            const next = swiperRef.current.querySelector('.swiper-button-next');
            if (prev) {
                next.classList.add('GtmModuleData-btn-common');
                prev.setAttribute('data-btn-id', '<');
                prev.addEventListener('click', () => handleGTM('<'));
            }
            if (next) {
                next.classList.add('GtmModuleData-btn-common');
                next.setAttribute('data-btn-id', '>');
                next.addEventListener('click', () => handleGTM('>'));
            }
            return () => {
                if (prev) {
                    prev.removeEventListener('click', () => handleGTM('<'));
                }
                if (next) {
                    next.removeEventListener('click', () => handleGTM('>'));
                }
            };
        }
    }, [swiperRef.current, swiper]);

    return (
        <GloLayout className={`editor-${prefixCls} editor-${prefixCls}-${id} theme-${theme}`} id={id} data={data} floorName={prefixCls}>
            <div className={`${prefixCls}-info`}>
                {title && (
                    <div className={`${prefixCls}-info-title ${enter?.title ? 'enter' : ''}`} style={{ color: titleColor }} title={title}>
                        {title}
                    </div>
                )}
                {desc && (
                    <div className={`${prefixCls}-info-desc ${enter?.desc ? 'enter' : ''}`} style={{ color: descColor }} title={desc}>
                        {desc}
                    </div>
                )}
            </div>
            <div className={`${prefixCls}-swiper-box`} ref={swiperRef}>
                <Swiper
                    className={`${prefixCls}-swiper`}
                    initialSlide={0}
                    spaceBetween={12}
                    slidesPerView={'auto'}
                    onSwiper={(swiperIns: ItfSwiper) => setSwiper(swiperIns)}
                    modules={[Navigation]}
                    navigation={true}
                >
                    {items.map((item: ItfServiceSupportItem, index: number) => {
                        const { text, url, img } = item;
                        const itemClassesA = classNames({
                            [`${prefixCls}-swiper-item`]: true,
                            [`${prefixCls}-swiper-item-${index}`]: true,
                            ['enter']: enter?.content,
                        });
                        return (
                            <SwiperSlide className={itemClassesA} key={`${index}`} style={{ transitionDelay: enter?.content ? `${index * 0.15}s` : '0s' }}>
                                {url && (
                                    <EcoLink
                                        className={`${prefixCls}-swiper-item-inner GtmModuleData-btn-href`}
                                        href={url}
                                        onClick={() => handle_GTM_Event(item)}
                                    >
                                        <div className={`${prefixCls}-swiper-item-img`}>
                                            <EcoImg pc={img} aria-label={item.text} alt={item.text} />
                                        </div>
                                        <div className={`${prefixCls}-swiper-item-content`}>
                                            {text && (
                                                <p className={`${prefixCls}-swiper-item-content-text`}>
                                                    <span title={text}>{text}</span>
                                                    <Arrow />
                                                </p>
                                            )}
                                        </div>
                                    </EcoLink>
                                )}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
        </GloLayout>
    );
}
